import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal } from '@material-ui/core';
import { Typography, Button, Loader, colors } from 'kangarootime-ui';
import RaisedContainer from '../../../../../components/RaisedContainer';
import Header from '../CreateChannelModal/Header';
import MultiItemSelect from '../../MultiItemSelect';
import { Mutation, graphql, compose } from 'react-apollo';
import { GET_DIRECT_MESSAGEABLE_EMPLOYEES, GET_DIRECT_MESSAGEABLE_PARENTS, GET_CHANNEL_MEMBERS } from '../../../graphql/queries';
import { ADD_NEW_MEMBERS_TO_CHANNEL } from '../../../graphql/mutations';

// dynamo's `batchWrite` is limited to 25 items per request
const MAX_NUMBER_OF_INVITES_PER_BATCH = 25;

const InviteAdditionalMembersToChannelModal = ({
  isOpen,
  onClose,
  currentCenterId,
  selectedChannelId,
  selectedChannel: { memberIds, broadcastType, broadcastTypeId, ...selectedChannel },
  // gql props
  employees: { getChannelMessageableUsers: messageableEmployees, ...employees },
  parents: { getDirectMessageableParentsAsEmployee: messageableParents, ...parents },
  ...props
}) => {
  if (!isOpen) {
    return null;
  }

  const [selectedUsers, setSelectedUsers] = useState([]);
  const [filterValue, setFilterValue] = useState('');
  const [selectedExistingChannelUsers, setSelectedExistingChannelUsers] = useState(false);
  const isBroadcastChannel = Boolean(broadcastType) && Boolean(broadcastTypeId);
  const employeesWithType = useMemo(() => messageableEmployees.map(emp => ({ ...emp, type: 'employee' })), []);
  const parentsWithType = useMemo(() => messageableParents.map(emp => ({ ...emp, type: 'parent' })), []);
  const dropdownItems = useMemo(() => isBroadcastChannel ? [...employeesWithType, ...parentsWithType] : [...employeesWithType], []);
  const filteredItems = dropdownItems.filter(u => u.name.toLowerCase().includes(filterValue));

  useEffect(() => {
    if (selectionContainsExistingChannelMembers()) {
      if (!selectedExistingChannelUsers) {
        setSelectedExistingChannelUsers(true);
      }
    } else {
      if (selectedExistingChannelUsers) {
        setSelectedExistingChannelUsers(false);
      }
    }
  }, [selectedUsers]);

  const selectionContainsExistingChannelMembers = () => {
    let invalidSelection = false;

    selectedUsers.forEach(user => {
      const chatId = `${user.type}-${user.id}`;

      if (memberIds.includes(chatId)) {
        invalidSelection = true;
      }
    });

    return invalidSelection;
  };

  /**
   * format the selected user array into an array of chat ids for graphql
   */
  const convertSelectedUsersToIdArray = () => {
    if (selectedUsers.length) {
      return selectedUsers.map(user => `${user.type}-${user.id}`);
    }

    return [];
  };

  /**
   * Update state if we're not above the max number
   * @param {String[]} items
   */
  const handleItemSelect = items => {
    if (selectedUsers.length === MAX_NUMBER_OF_INVITES_PER_BATCH) {
      // removing items, update
      if (items.length < selectedUsers.length) {
        setSelectedUsers(items);
      }

      return;
    }

    setSelectedUsers(items);
  };

  return (
    <Modal open={isOpen} onClose={onClose}>
      <RaisedContainer className='flex-columns' style={{ height: '100%' }}>
        <Header closeModal={onClose} />
        <div className='flex' style={{ justifyContent: 'center', padding: '15vh 2rem 2rem' }}>
          <div className='flex-columns' style={{ width: 640, maxWidth: '100%' }}>
            <Typography>Invite Additional Members to this channel</Typography>
            { selectedExistingChannelUsers && <Typography type='bold' style={{ color: colors.danger }}>One or more of selected users are already members of this channel</Typography> }
            <MultiItemSelect
              label={selectedUsers.length > 0 ? `(You can invite ${Math.max(MAX_NUMBER_OF_INVITES_PER_BATCH - selectedUsers.length, 0)} more people)` : ''}
              items={dropdownItems}
              filteredItems={filteredItems}
              onItemSelect={handleItemSelect}
              onFilter={filterValue => setFilterValue(filterValue)}
              dropdownItemKeyExtractor={item => `${item.type}-${item.id}`}
              disableSelection={selectedUsers.length === MAX_NUMBER_OF_INVITES_PER_BATCH}
            />
            <Mutation
              mutation={ADD_NEW_MEMBERS_TO_CHANNEL}
              refetchQueries={[{ query: GET_CHANNEL_MEMBERS, variables: { channelId: selectedChannelId } }]}
              onCompleted={onClose}
            >
              {(addNewMembersToChannel, { loading }) => (
                <Loader loading={loading}>
                  <Button
                    disabled={selectedUsers.length === 0 || selectionContainsExistingChannelMembers()}
                    onClick={() => {
                      const ids = convertSelectedUsersToIdArray();
                      addNewMembersToChannel({
                        variables: {
                          ids,
                          channelId: selectedChannelId
                        }
                      });
                    }}
                    style={{ marginTop: 8, alignSelf: 'flex-end' }}
                  >
                    Add People
                  </Button>
                </Loader>
              )}
            </Mutation>
          </div>
        </div>
      </RaisedContainer>
    </Modal>
  );
};

InviteAdditionalMembersToChannelModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  currentCenterId: PropTypes.string,
  selectedChannelId: PropTypes.string.isRequired,
  selectedChannel: PropTypes.object.isRequired
};

export default compose(
  graphql(GET_DIRECT_MESSAGEABLE_PARENTS, {
    name: 'parents',
    options: props => ({
      fetchPolicy: 'cache-first',
      variables: {
        centerId: props.currentCenterId
      }
    })
  }),
  graphql(GET_DIRECT_MESSAGEABLE_EMPLOYEES, {
    name: 'employees',
    options: props => ({
      fetchPolicy: 'cache-first',
      variables: {
        centerId: props.currentCenterId
      }
    })
  })
)(InviteAdditionalMembersToChannelModal);
